import { pointerToken } from "./documentDiff.js";

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Objects recurse; arrays and scalars are leaves. A nested empty object is a
// leaf so that adding or removing one still counts; the root is not.
function flatten(value, pointer, out) {
    if (!isPlainObject(value)) {
        out.set(pointer || "/", value);
        return out;
    }
    const entries = Object.entries(value);
    if (!entries.length && pointer) out.set(pointer, value);
    for (const [key, child] of entries) flatten(child, `${pointer}/${pointerToken(key)}`, out);
    return out;
}

/// Leaf-level changes between two documents. Unlike
/// `managedConfigDocumentChanges`, a key added under a new parent is reported
/// at its own pointer, so a subsection's pointer prefix always finds it.
///
/// `memberPointers` names arrays whose members belong to different
/// subsections. Each added or removed member is one change, carrying `member`.
export function documentLeafChanges(before, after, { memberPointers = [] } = {}) {
    const left = flatten(isPlainObject(before) ? before : {}, "", new Map());
    const right = flatten(isPlainObject(after) ? after : {}, "", new Map());
    const changes = [];
    for (const pointer of new Set([...left.keys(), ...right.keys()])) {
        const beforeFound = left.has(pointer);
        const afterFound = right.has(pointer);
        const beforeValue = left.get(pointer);
        const afterValue = right.get(pointer);
        if (beforeFound && afterFound && JSON.stringify(beforeValue) === JSON.stringify(afterValue)) continue;
        const memberwise = memberPointers.includes(pointer)
            && (!beforeFound || Array.isArray(beforeValue))
            && (!afterFound || Array.isArray(afterValue));
        if (memberwise) {
            const beforeMembers = beforeFound ? beforeValue : [];
            const afterMembers = afterFound ? afterValue : [];
            for (const member of new Set([...beforeMembers, ...afterMembers])) {
                const inBefore = beforeMembers.includes(member);
                const inAfter = afterMembers.includes(member);
                if (inBefore === inAfter) continue;
                changes.push({ pointer, member, before: member, after: member, beforeFound: inBefore, afterFound: inAfter });
            }
            continue;
        }
        changes.push({ pointer, before: beforeValue, after: afterValue, beforeFound, afterFound });
    }
    return changes;
}

/// Whether `change` falls under one subsection pointer spec. A spec is a
/// pointer prefix, or `{ pointer, members }` / `{ pointer, exceptMembers }`
/// for an array that subsections share.
export function pointerSpecMatches(spec, change) {
    const prefix = typeof spec === "string" ? spec : spec.pointer;
    if (change.pointer !== prefix && !change.pointer.startsWith(`${prefix}/`)) return false;
    if (typeof spec === "string") return true;
    if (spec.members) return change.member !== undefined && spec.members.includes(change.member);
    if (spec.exceptMembers) return change.member === undefined || !spec.exceptMembers.includes(change.member);
    return true;
}

function matchesAny(specs, change) {
    return specs.some((spec) => pointerSpecMatches(spec, change));
}

/// Attribute each change to the subsection whose pointers cover it. Changes to
/// accepted settings that have no control, and to settings the catalog no
/// longer accepts, are returned separately so that nothing goes uncounted.
export function tallyChanges(definition, changes) {
    const subsections = new Map();
    const categories = new Map();
    const preserved = [];
    const unaccepted = [];
    for (const category of definition.categories) {
        categories.set(category.id, 0);
        for (const subsection of category.subsections) subsections.set(subsection.id, []);
    }
    for (const change of changes) {
        let owner = null;
        for (const category of definition.categories) {
            owner = category.subsections.find((subsection) => matchesAny(subsection.pointers, change));
            if (owner) {
                subsections.get(owner.id).push(change);
                categories.set(category.id, categories.get(category.id) + 1);
                break;
            }
        }
        if (owner) continue;
        if (matchesAny(definition.preservedPointers ?? [], change)) preserved.push(change);
        else unaccepted.push(change);
    }
    return { subsections, categories, preserved, unaccepted, total: changes.length };
}
